import React from 'react';
import { MAX_INVESTMENT_AMOUNT_USD } from '../../../constants/profileFieldLimits';
import { formatCompactAmount } from '../../../utils/formatCompactAmount';

interface RangeSliderProps {
  minValue: number;
  maxValue: number;
  onMinChange: (amount: number) => void;
  onMaxChange: (amount: number) => void;
  min?: number;
  max?: number;
  step?: number;
  multiplier?: number; // e.g., 10x
  currency?: string;
  isMultiplierActive?: boolean;
  onMultiplierToggle?: () => void;
  /**
   * When true (default), active +10x multiplies displayed values.
   * When false, values are already true amounts and +10x only unlocks a higher ceiling.
   */
  multiplyDisplay?: boolean;
  hideRangeText?: boolean;
  scaleExponent?: number; // < 1 gives more visual space to low values (e.g. 0.4)
  isEmpty?: boolean; // when true: both handles at left, bar fully gray, inputs blank
}

const formatNumber = (num: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(num);

const RangeSlider: React.FC<RangeSliderProps> = ({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  min = 0,
  max = MAX_INVESTMENT_AMOUNT_USD,
  step = 1000,
  multiplier = 1,
  currency = 'EUR',
  isMultiplierActive = false,
  onMultiplierToggle,
  multiplyDisplay = true,
  hideRangeText = false,
  scaleExponent = 1,
  isEmpty = false,
}) => {
  // An unset maximum is stored as 0. Equal min/max (100,000 to 100,000) is a real range.
  const maxIsUnset = isEmpty || !(maxValue > 0);
  const safeMinValue = maxIsUnset ? minValue : Math.min(minValue, maxValue);
  const safeMaxValue = maxIsUnset ? minValue : Math.max(minValue, maxValue);

  const scaleActive = Boolean(multiplyDisplay && isMultiplierActive && multiplier > 1);
  const displayedMin = scaleActive ? safeMinValue * multiplier : safeMinValue;
  const displayedMax = scaleActive ? safeMaxValue * multiplier : safeMaxValue;

  const sliderRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState<'min' | 'max' | null>(null);

  // Local string state for inputs
  const [inputMin, setInputMin] = React.useState(isEmpty ? '' : formatNumber(displayedMin));
  const [inputMax, setInputMax] = React.useState(isEmpty ? '' : formatNumber(displayedMax));
  const [maxTouched, setMaxTouched] = React.useState(!isEmpty);
  const minFocused = React.useRef(false);
  const maxFocused = React.useRef(false);

  React.useEffect(() => {
    if (isEmpty) {
      setMaxTouched(false);
      return;
    }
    // A finished range — including 100,000 to 100,000 — should show the maximum.
    if (maxValue > 0) {
      setMaxTouched(true);
    }
  }, [isEmpty, maxValue]);

  // Sync slider → inputs when not focused. Max stays blank until the user types it.
  React.useEffect(() => {
    if (!minFocused.current) setInputMin(isEmpty ? '' : formatNumber(displayedMin));
  }, [displayedMin, isEmpty]);

  React.useEffect(() => {
    if (!maxTouched) {
      setInputMax('');
      return;
    }
    if (!maxFocused.current) setInputMax(formatNumber(displayedMax));
  }, [displayedMax, isEmpty, maxTouched]);

  // Parse typed value to raw slider value (removes multiplier scale and commas)
  const parseToRaw = (raw: string): number | null => {
    const cleaned = raw.replace(/[,\s]/g, '');
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed) || parsed < 0) return null;
    return scaleActive ? parsed / multiplier : parsed;
  };

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputMin(val);
    const rawValue = parseToRaw(val);
    if (rawValue !== null) {
      const stepped = Math.round(rawValue / step) * step;
      const ceiling = maxTouched ? safeMaxValue : max;
      // Allow 0 / below-minimum while typing; save-time validation enforces the floor.
      const clamped = Math.max(0, Math.min(stepped, ceiling));
      onMinChange(clamped);
    }
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputMax(val);
    setMaxTouched(true);
    const rawValue = parseToRaw(val);
    if (rawValue !== null) {
      const stepped = Math.round(rawValue / step) * step;
      const clamped = Math.max(safeMinValue, Math.min(stepped, max));
      onMaxChange(clamped);
    }
  };

  const handleMinBlur = () => {
    minFocused.current = false;
    if (inputMin.trim() === '') {
      onMinChange(0);
      setInputMin(isEmpty ? '' : '0');
      return;
    }
    const rawValue = parseToRaw(inputMin);
    if (rawValue !== null) {
      const stepped = Math.round(rawValue / step) * step;
      const ceiling = maxTouched ? safeMaxValue : max;
      const clamped = Math.max(0, Math.min(stepped, ceiling));
      onMinChange(clamped);
      setInputMin(formatNumber(scaleActive ? clamped * multiplier : clamped));
    } else {
      setInputMin(isEmpty ? '' : formatNumber(displayedMin));
    }
  };

  const handleMaxBlur = () => {
    maxFocused.current = false;
    const rawValue = parseToRaw(inputMax);
    if (rawValue !== null) {
      const stepped = Math.round(rawValue / step) * step;
      const clamped = Math.max(safeMinValue, Math.min(stepped, max));
      onMaxChange(clamped);
      setInputMax(formatNumber(scaleActive ? clamped * multiplier : clamped));
    } else {
      setInputMax(isEmpty ? '' : formatNumber(displayedMax));
    }
  };

  // Non-linear scale helpers
  const valueToPercent = (value: number): number => {
    if (max === min) return 0;
    return Math.pow((value - min) / (max - min), scaleExponent) * 100;
  };
  const percentToValue = (pct: number): number => {
    return min + Math.pow(pct / 100, 1 / scaleExponent) * (max - min);
  };

  const shownMaxValue = maxTouched ? safeMaxValue : safeMinValue;
  const minPercent = isEmpty ? 0 : valueToPercent(safeMinValue);
  const maxPercent = isEmpty || (maxIsUnset && !maxTouched) ? 0 : valueToPercent(shownMaxValue);

  const handleStart = (e: React.MouseEvent | React.TouchEvent, handle: 'min' | 'max') => {
    setIsDragging(handle);
    e.preventDefault();
  };

  const handleMove = React.useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !sliderRef.current) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const rect = sliderRef.current.getBoundingClientRect();
      const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const rawValue = percentToValue(pct);
      const newValue = Math.round(rawValue / step) * step;

      if (isDragging === 'min') {
        const ceiling = maxValue > 0 ? safeMaxValue : max;
        onMinChange(Math.max(min, Math.min(newValue, ceiling)));
      } else {
        setMaxTouched(true);
        onMaxChange(Math.max(min, Math.min(Math.max(newValue, safeMinValue), max)));
      }
    },
    [isDragging, safeMinValue, safeMaxValue, onMinChange, onMaxChange, min, max, step, maxValue]
  );

  const handleEnd = React.useCallback(() => setIsDragging(null), []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!sliderRef.current || isDragging) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const rawValue = percentToValue(pct);
    const newValue = Math.round(rawValue / step) * step;
    const minDistance = Math.abs(newValue - safeMinValue);
    const maxDistance = Math.abs(newValue - safeMaxValue);

    if (maxValue > 0 && minDistance < maxDistance) {
      onMinChange(Math.min(newValue, safeMaxValue));
    } else if (maxValue > 0) {
      setMaxTouched(true);
      onMaxChange(Math.max(safeMinValue, Math.min(newValue, max)));
    } else if (newValue < safeMinValue) {
      onMinChange(Math.max(min, Math.min(newValue, max)));
    } else {
      setMaxTouched(true);
      onMaxChange(Math.max(safeMinValue, Math.min(newValue, max)));
    }
  };

  const typedRaw = parseToRaw(inputMin);
  const typedDisplayed = typedRaw === null ? null : scaleActive ? typedRaw * multiplier : typedRaw;
  const showMinimumInvestmentHint =
    isEmpty || (typedDisplayed !== null && typedDisplayed < min) || displayedMin < min;
  const minimumInvestmentLabel = formatNumber(scaleActive ? min * multiplier : min);

  return (
    <div className="space-y-2">
      {/* Manual Inputs */}
      <div className="flex items-end justify-between w-full">
        <div className="w-28">
          <p className="text-white font-semibold text-xs mb-1 uppercase text-center">
            Min {currency.toUpperCase()}
          </p>
          <input
            type="text"
            inputMode="numeric"
            value={inputMin}
            onChange={handleMinInputChange}
            onFocus={() => {
              minFocused.current = true;
            }}
            onBlur={handleMinBlur}
            placeholder="0"
            className="w-full bg-transparent text-white text-sm rounded px-3 py-2 border border-cyan-500 focus:outline-none"
          />
        </div>
        <span className="text-gray-400 flex-shrink-0 pb-2">to</span>
        <div className="w-28">
          <p className="text-white font-semibold text-xs mb-1 uppercase text-center">
            Max {currency.toUpperCase()}
          </p>
          <input
            type="text"
            inputMode="numeric"
            value={inputMax}
            onChange={handleMaxInputChange}
            onFocus={() => {
              maxFocused.current = true;
            }}
            onBlur={handleMaxBlur}
            placeholder="0"
            className="w-full bg-transparent text-white text-sm rounded px-3 py-2 border border-cyan-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Dual Range Slider */}
      <div className="flex items-center gap-2 w-full">
        <div className="flex-grow relative" ref={sliderRef}>
          <div
            className="relative h-2 bg-gray-500 rounded-lg cursor-pointer"
            onClick={handleTrackClick}
          >
            {!isEmpty && (
              <div
                className="absolute h-2 bg-cyan-500 rounded-lg"
                style={{ left: `${minPercent}%`, width: `${maxPercent - minPercent}%` }}
              />
            )}
            <div
              className="absolute w-4 h-4 bg-gray-300 rounded border-2 border-gray-400 cursor-grab active:cursor-grabbing -mt-1"
              style={{
                left: `calc(${minPercent}% - 8px)`,
                zIndex: isDragging === 'min' ? 2 : 1,
              }}
              onMouseDown={e => handleStart(e, 'min')}
              onTouchStart={e => handleStart(e, 'min')}
            />
            <div
              className="absolute w-4 h-4 bg-gray-300 rounded border-2 border-gray-400 cursor-grab active:cursor-grabbing -mt-1"
              style={{
                left: `calc(${maxPercent}% - 8px)`,
                zIndex: isDragging === 'max' ? 2 : 1,
              }}
              onMouseDown={e => handleStart(e, 'max')}
              onTouchStart={e => handleStart(e, 'max')}
            />
          </div>
        </div>
        {multiplier !== 1 && (
          <button
            type="button"
            onClick={onMultiplierToggle}
            className={`px-3 py-1 text-sm rounded-md bg-gray-700 text-white border transition-colors ${
              isMultiplierActive
                ? 'border-emerald-400 shadow-[0_0_0_2px_rgba(146,208,80,0.3)_inset]'
                : 'border-gray-600'
            }`}
          >
            +{multiplier}x
          </button>
        )}
      </div>

      {/* Range Display */}
      {!hideRangeText && !isEmpty && maxTouched && (
        <div className="text-white font-semibold uppercase text-right">
          Range: {formatCompactAmount(displayedMin)} - {formatCompactAmount(displayedMax)}{' '}
          {currency.toUpperCase()}
        </div>
      )}

      {/* Floor hint while empty or typed min is below the allowed minimum (auto-clamps on blur) */}
      {showMinimumInvestmentHint && (
        <p className="text-yellow-500 text-sm mt-2 text-left">
          Minimum investment: {minimumInvestmentLabel}
        </p>
      )}
    </div>
  );
};

export default RangeSlider;
